// ЧАРТ ШАГА 6 — силуэт-заливка и красная поверх. B8-4 `3e619875cbb881e38637ca9eeacd3abd`, решение `s963.d1`.
//
// ИСТОЧНИК — `chart(bio,'terrain',356,236,{})` из движка борда шага 6 (`docs/design/wizard-s6/`, борд
// `Life Map - s6 Overlay v10.html` `:63`). Порт байт в байт: та же разметка SVG, те же числа, та же
// интерполяция (`sm()`, t = .85, `toFixed(2)`). Эталон исполняется из байта борда в тесте.
//
// ⛔ НЕ `s5Chart.js` (B7-2): у шага 5 ось — колонки этапов (Catmull-Rom t = .5 через середины
// колонок, `toFixed(1)`), у шага 6 — время (`geom()`: x по датам этапа, концы кривой прибиты к краям
// полосы). Геометрия разная, общего кода нет — `s963.d1` п. 2.
//
// Отличия от байта — три, каждое названо в тесте:
//   1. токен amber в `tg`: `#A86520` (канон, `WIZARD-CANON.md:75`), у движка борда `#A87326`;
//   2. `NOW` — не константа борда `2026+7/12`, а `nowY` сессии (конец текущего этапа, модель А);
//      потолок год-тиков `yy<2026` → `yy<floor(nowY)` (при NOW борда — то же число);
//   3. `null`-этап (`s836.d3`): кривые идут по `filled()` (внутри линейно, голова/хвост плоско);
//      ember-точки у `null` несущей нет — нет значения, которое она бы отметила. Байт `null` не знает.
//
// ЧИСТАЯ ФУНКЦИЯ: на входе числа, на выходе строка SVG. Значения несущей — `redLine.carrier` движка,
// силуэт — `silhouetteOf` (`src/render/silhouette.js`), здесь не пересчитываются.

import { filled } from '../../insights/input.js';

/** Размер чарта борда: `chart(…,'terrain',356,236,{})` — v10 `:63`. Высота SVG = H + 26 (тики). */
export const CHART_W = 356;
export const CHART_H = 236;
/** Токен amber середины заливки (`WIZARD-CANON.md:75`); у движка борда — `#A87326`. */
export const AMBER = '#A86520';
const ID = 's6terrain';

// `sm()` движка — Catmull-Rom с натяжением t, `toFixed(2)`.
const sm = (p, t) => {
  if (p.length < 2) return '';
  let d = `M${p[0].x.toFixed(2)},${p[0].y.toFixed(2)}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[Math.max(i - 1, 0)], p1 = p[i], p2 = p[i + 1], p3 = p[Math.min(i + 2, p.length - 1)];
    const c1x = p1.x + (p2.x - p0.x) / 6 * t, c1y = p1.y + (p2.y - p0.y) / 6 * t;
    const c2x = p2.x - (p3.x - p1.x) / 6 * t, c2y = p2.y - (p3.y - p1.y) / 6 * t;
    d += `C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
  }
  return d;
};

/**
 * `geom()` движка. Этапы — `{ startY, endY }` в десятичных годах (`toDecimalYear`), конец текущего —
 * `nowY`. Ось Y — как в `chart()`: ±3 на полувысоту минус 16.
 */
export function geom(stages, nowY, W = CHART_W, H = CHART_H, pad = 10) {
  const t0 = stages[0].startY, t1 = nowY, L = pad, R = W - 4;
  const end = (s, i) => (i === stages.length - 1 ? nowY : s.endY);
  const x = (t) => L + (t - t0) / (t1 - t0) * (R - L);
  const y = (v) => H / 2 - (v / 3) * (H / 2 - 16);
  const mid = stages.map((s, i) => x((s.startY + end(s, i)) / 2));
  const bnd = stages.map((s) => x(s.startY));
  return { x, y, mid, bnd, t0, t1, L, R, zero: y(0) };
}

// `curve()` движка: концы прибиты к краям полосы на уровне первого/последнего значения.
function curve(vals, g) {
  const p = [{ x: g.L, y: g.y(vals[0]) }, ...vals.map((v, i) => ({ x: g.mid[i], y: g.y(v) })), { x: g.R, y: g.y(vals[vals.length - 1]) }];
  return { pts: p, d: sm(p, 0.85) };
}

/**
 * Данные чарта из паспорта: даты этапов, силуэт, несущая. `null`, если хотя бы у одного этапа нет
 * даты старта — временной оси тогда нет (парковка `(в)` DoR s963; путь визарда такого не даёт,
 * `toWizardInput` бросает на недатированной строке).
 */
export function chartInput(passport, nowY) {
  const stages = (passport.input && passport.input.wizard && passport.input.wizard.stages) || [];
  const silhouette = passport.silhouette || [];
  const carrier = (passport.system1 && passport.system1.redLine && passport.system1.redLine.carrier) || [];
  const now = Number.isFinite(nowY) ? nowY : stages.length && stages[stages.length - 1].endY;
  if (!stages.length || !Number.isFinite(now) || stages.some((s) => !Number.isFinite(s.startY))) return null;
  if (silhouette.length !== stages.length || carrier.length !== stages.length) return null;
  return { stages, nowY: now, silhouette, carrier };
}

/**
 * Разметка SVG — `chart(bio,'terrain',W,H,{})` движка борда, ветка не-mini без `soloRed`/`soloSil`.
 * @param {{stages:{startY:number,endY:number|null}[], nowY:number, silhouette:(number|null)[], carrier:(number|null)[]}} input
 */
export function chartSvg(input, { W = CHART_W, H = CHART_H, id = ID, amber = AMBER } = {}) {
  const { stages, nowY } = input;
  const g = geom(stages, nowY, W, H, 10), u = id;
  const silV = filled(input.silhouette), redV = filled(input.carrier);
  const sil = curve(silV, g), red = curve(redV, g);
  const area = sil.d + `L${g.R},${g.zero}L${g.L},${g.zero}Z`;
  const yrs = []; for (let yy = Math.ceil(g.t0 / 4) * 4; yy < Math.floor(nowY); yy += 4) yrs.push(yy);
  const sw = 3.2, dw = 5.5;
  return `<svg viewBox="0 0 ${W} ${H + 26}" style="width:100%;display:block" preserveAspectRatio="none">
 <defs>
  <linearGradient id="tg${u}" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="${H}"><stop offset="0" stop-color="#D48A3C" stop-opacity=".48"/><stop offset=".52" stop-color="${amber}" stop-opacity=".2"/><stop offset="1" stop-color="#C8482B" stop-opacity=".30"/></linearGradient>
  <linearGradient id="rg${u}" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${W}" y2="0"><stop offset="0" stop-color="#7A1E10" stop-opacity=".5"/><stop offset=".3" stop-color="#C8482B"/><stop offset=".75" stop-color="#E8623F"/><stop offset="1" stop-color="#C8482B" stop-opacity=".8"/></linearGradient>
  <filter id="gl${u}" x="-10%" y="-40%" width="120%" height="180%"><feGaussianBlur stdDeviation="3.2"/></filter>
 </defs>
 <path d="${area}" fill="url(#tg${u})"/><path d="${sil.d}" fill="none" stroke="rgba(212,138,60,.55)" stroke-width="1.2"/>
 ${'' /* ветка `lines` движка — пуста в terrain */}
 <line x1="0" y1="${g.zero}" x2="${W}" y2="${g.zero}" stroke="rgba(232,226,212,.2)" stroke-dasharray="2 6" stroke-width=".6"/>
 <path d="${red.d}" fill="none" stroke="#C8482B" stroke-width="${dw * 1.7}" opacity=".2" filter="url(#gl${u})"/><path d="${red.d}" fill="none" stroke="#0a0b0d" stroke-width="${sw * 2.1}" opacity=".6" stroke-linecap="round"/><path d="${red.d}" fill="none" stroke="url(#rg${u})" stroke-width="${sw}" stroke-linecap="round"/>
 ${input.carrier.map((v, i) => (v == null ? '' : `<circle cx="${g.mid[i]}" cy="${g.y(v)}" r="11" fill="rgba(200,72,43,.16)"/><circle cx="${g.mid[i]}" cy="${g.y(v)}" r="4.6" fill="#C8482B" stroke="#0a0b0d" stroke-width="1.6"/>`)).join('')}
 <g font-family="JetBrains Mono, monospace" font-size="9.5" letter-spacing="1.4">${yrs.map((yy) => `<text x="${g.x(yy)}" y="${H + 18}" text-anchor="middle" fill="#4e4a43">'${String(yy).slice(-2)}</text>`).join('')}<text x="${W - 2}" y="${H + 18}" text-anchor="end" fill="#8F887A">now</text></g>
 ${stages.slice(1).map((s, i) => `<line x1="${g.bnd[i + 1]}" y1="6" x2="${g.bnd[i + 1]}" y2="${H - 6}" stroke="rgba(232,226,212,.07)" stroke-width="1"/>`).join('')}
 </svg>`;
}
